/**
 * 利用規約・プライバシーポリシーの改定の告知（「確認」でよい改定）
 *
 * - 同意が要る改定は、これではなく同意画面（pages/Consent.tsx。OnboardingGuard が送る）
 * - 確認でよい改定は、止めずにこの告知を出す。アプリの画面（公開ページ以外）を開いたとき、
 *   ブラウザのセッションごとに 1 回だけ自動で開く。「あとで」で閉じても、AI アシスタントの入口から開き直せる
 * - 「内容を確認しました」で、確認したこと（版・日時・この画面から）を記録する（PUT /api/user/consent）
 *
 * 根拠と設計は docs/decisions/policy-change-notice.md。
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import PolicyUpdateNotice from '@/components/policy/PolicyUpdateNotice';
import { PolicyNoticeContext } from '@/contexts/policyNoticeContextObj';
import { useAuth } from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';
import { POLICY_ACTION } from '@/utils/policyStatus';
import { isPublicPath } from '@/utils/publicRoutes';

// 同意画面・プロフィール入力では出さない（そちらを先に済ませてもらう）
const ONBOARDING_PATHS = new Set(['/consent', '/complete-profile']);

function dismissedKey(userId: string, versions: string): string {
  return `policy-notice-dismissed:${userId}:${versions}`;
}

function readDismissed(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // 保存できなくても閉じる（次に画面を開いたときにまた出るだけ）
  }
}

export function PolicyNoticeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { policyStatuses, isConsentSatisfied } = useUser();
  const location = useLocation();
  const pending = useMemo(
    () => (isConsentSatisfied ? policyStatuses.filter((s) => s.action === POLICY_ACTION.ACKNOWLEDGE) : []),
    [policyStatuses, isConsentSatisfied],
  );
  const versions = pending.map((s) => `${s.doc}@${s.currentVersion}`).join(',');
  const key = user?.id && versions ? dismissedKey(user.id, versions) : '';
  // 「あとで」を押した組（利用者 × 版）。ブラウザのセッションに残した分は、組が変わったときに読む
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
  const [manualOpen, setManualOpen] = useState(false);
  const storedDismissed = useMemo(() => (key ? readDismissed(key) : false), [key]);

  const isDismissed = key ? (dismissed[key] ?? storedDismissed) : true;
  const onAppPage = !isPublicPath(location.pathname) && !ONBOARDING_PATHS.has(location.pathname);
  const open = pending.length > 0 && (manualOpen || (onAppPage && !isDismissed));

  const later = useCallback(() => {
    setManualOpen(false);
    if (!key) return;
    setDismissed((current) => ({ ...current, [key]: true }));
    writeDismissed(key);
  }, [key]);

  const hasPending = pending.length > 0;
  const openNotice = useCallback(() => setManualOpen(true), []);
  const value = useMemo(() => ({ hasPending, openNotice }), [hasPending, openNotice]);

  return (
    <PolicyNoticeContext.Provider value={value}>
      {children}
      {pending.length > 0 && (
        <PolicyUpdateNotice
          open={open}
          statuses={pending}
          onLater={later}
          onAcknowledged={() => setManualOpen(false)}
        />
      )}
    </PolicyNoticeContext.Provider>
  );
}
